import { useEffect, useState, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { BUCKET_META } from "@/components/FulfillmentOverview";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader, StatusBadge, EmptyState, money, fmtDate, carrierLabel, CarrierBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ClientSchedule } from "@/components/ClientSchedule";
import { toast } from "sonner";
import { Plus, CaretRight, MagnifyingGlass, Eye, PencilSimple, ArrowCounterClockwise, ArrowsClockwise } from "@phosphor-icons/react";
import { ConsolidateOrdersButton } from "@/components/ConsolidateOrdersDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const FILTER_KEY = "orders.filters";
const readFilters = () => { try { return JSON.parse(sessionStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; } };

const STATUS_GROUPS = [
  { key: "status", label: "Approval", fallback: "pending", options: [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]] },
  { key: "procurement_status", label: "Procurement", fallback: "not_started", options: [["not_started", "Not started"], ["sourcing", "Sourcing"], ["ordered", "PO placed"], ["received", "Received"]] },
  { key: "shipping_status", label: "Shipping", fallback: "not_shipped", options: [["not_shipped", "Not shipped"], ["preparing", "Preparing"], ["dispatched", "Dispatched"], ["collected", "Collected"], ["in_transit", "In transit"], ["arrived", "Arrived"], ["delivered", "Delivered"]] },
];

export default function Orders() {
  const { isTeam } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const savedFilters = readFilters();
  const [params, setParams] = useSearchParams();
  const fulfil = params.get("fulfil");
  const [fulfilIds, setFulfilIds] = useState(null);
  useEffect(() => {
    if (!fulfil || !BUCKET_META[fulfil]) { setFulfilIds(null); return; }
    api.get("/stats/fulfillment").then((r) => setFulfilIds(new Set(r.data.buckets[fulfil]?.order_ids || []))).catch(() => setFulfilIds(new Set()));
  }, [fulfil]);
  const clearFulfil = () => { params.delete("fulfil"); setParams(params, { replace: true }); };
  const [search, setSearch] = useState(() => savedFilters.search || "");
  const [clientFilter, setClientFilter] = useState(() => params.get("client") || savedFilters.clientFilter || "all");
  const [tabs, setTabs] = useState(() => savedFilters.tabs || { status: "all", procurement_status: "all", shipping_status: "all" });
  const setTab = (k, v) => setTabs((p) => ({ ...p, [k]: p[k] === v ? "all" : v }));
  const [needsPo, setNeedsPo] = useState(() => !!savedFilters.needsPo);
  useEffect(() => { sessionStorage.setItem(FILTER_KEY, JSON.stringify({ search, clientFilter, tabs, needsPo })); }, [search, clientFilter, tabs, needsPo]);
  const hasFilters = search || clientFilter !== "all" || needsPo || Object.values(tabs).some((v) => v !== "all");
  const clearFilters = () => { setSearch(""); setClientFilter("all"); setNeedsPo(false); setTabs({ status: "all", procurement_status: "all", shipping_status: "all" }); };

  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  const [viewOrder, setViewOrder] = useState(null);
  useEffect(() => { if (bodyRef.current) setScrollW(bodyRef.current.scrollWidth); }, [orders, isTeam]);
  const syncTop = () => { if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncBody = () => { if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft; };
  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (isTeam) {
      api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
      api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    }
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const posFor = (id) => pos.filter((p) => p.order_id === id);
  const invoicesFor = (id) => invoices.filter((i) => i.order_id === id || (i.order_ids || []).includes(id));

  const removeOrder = async (id, number) => {
    await api.delete(`/orders/${id}`);
    toast.success(`Order ${number} deleted`);
    load();
  };

  const removeClientOrder = async (id, number) => {
    await api.delete(`/orders/${id}/client-delete`);
    toast.success(`Order ${number} deleted`);
    load();
  };

  const q = search.trim().toLowerCase();
  const clients = [...new Set(orders.map((o) => o.client_name).filter(Boolean))].sort();
  const poOrderIds = new Set(pos.map((p) => p.order_id));
  const isNeedsPo = (o) => o.status === "approved" && !poOrderIds.has(o.id) && (o.procurement_status || "not_started") !== "received";
  const filtered = orders.filter((o) => (clientFilter === "all" || o.client_name === clientFilter) &&
    (!fulfilIds || fulfilIds.has(o.id)) &&
    (!needsPo || isNeedsPo(o)) &&
    STATUS_GROUPS.every((g) => tabs[g.key] === "all" || (o[g.key] || g.fallback) === tabs[g.key]) &&
    (!q || [o.patient_name, o.order_number, o.client_name, o.client_reference, ...(o.items || []).map((i) => `${i.name} ${i.sku || ""}`)].some((v) => (v || "").toLowerCase().includes(q))));

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle={isTeam ? "All client orders" : t("orders.subtitleClient")}
        title={isTeam ? "Orders" : t("orders.title")}
        actions={
          <div className="flex gap-2">
            {!isTeam && <ConsolidateOrdersButton />}
            {isTeam && <Button variant="outline" className="rounded-sm" onClick={() => navigate("/transit-shipments")} data-testid="transit-shipments-button"><ArrowsClockwise size={16} className="mr-2" /> Transit shipments</Button>}
            <Button className="rounded-sm" onClick={() => navigate("/orders/new")} data-testid="place-order-button">
              <Plus size={16} className="mr-2" /> {isTeam ? "New Order" : t("orders.placeOrder")}
            </Button>
          </div>
        }
      />

      {!isTeam && <ClientSchedule />}

      {fulfil && BUCKET_META[fulfil] && (
        <div className="flex items-center gap-2 text-sm rounded-sm border px-3 py-2" style={{ borderColor: BUCKET_META[fulfil].color, background: `${BUCKET_META[fulfil].color}14` }} data-testid="fulfil-filter-chip">
          <span>Showing <strong>{BUCKET_META[fulfil].label}</strong> orders{fulfilIds ? ` (${fulfilIds.size})` : ""}</span>
          <button className="ml-auto underline text-muted-foreground" onClick={clearFulfil} data-testid="fulfil-filter-clear">Show all orders</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9 rounded-sm"
            placeholder={isTeam ? "Search by product, patient, ref #, order # or client…" : t("orders.searchPh")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="orders-search"
          />
        </div>
        {isTeam && clients.length > 0 && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="rounded-sm w-full sm:w-56" data-testid="orders-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c} value={c} data-testid={`orders-client-opt-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {isTeam && hasFilters && (
          <>
            <span className="text-xs text-muted-foreground" data-testid="orders-filter-count">{filtered.length} of {orders.length}</span>
            <button type="button" onClick={clearFilters} className="text-xs text-primary hover:underline" data-testid="orders-clear-filters">Clear filters</button>
          </>
        )}
      </div>

      {isTeam && (
        <div className="space-y-2" data-testid="orders-status-tabs">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground w-24 shrink-0">Attention</span>
            <button type="button" onClick={() => setNeedsPo((v) => !v)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-sm border transition-colors ${needsPo ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 border-amber-200 text-amber-800 hover:border-amber-400"}`}
              data-testid="orders-tab-needs-po">
              Approved but no PO yet <span className={`ml-1 tabular-nums ${needsPo ? "opacity-90" : "opacity-70"}`}>{orders.filter(isNeedsPo).length}</span>
            </button>
          </div>
          {STATUS_GROUPS.map((g) => (
            <div key={g.key} className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground w-24 shrink-0">{g.label}</span>
              {g.options.map(([v, l]) => {
                const count = orders.filter((o) => (o[g.key] || g.fallback) === v).length;
                const active = tabs[g.key] === v;
                return (
                  <button key={v} type="button" onClick={() => setTab(g.key, v)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"}`}
                    data-testid={`orders-tab-${g.key}-${v}`}>
                    {l} <span className={`ml-1 tabular-nums ${active ? "opacity-80" : "opacity-60"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {!loading && orders.length === 0 ? (
        <EmptyState
          title={isTeam ? "No orders yet" : t("orders.noneTitle")}
          hint={isTeam ? "Client orders will appear here for approval and fulfilment." : t("orders.noneHintClient")}
          action={<Button className="rounded-sm" onClick={() => navigate("/orders/new")}>{isTeam ? "New Order" : t("orders.placeOrder")}</Button>}
        />
      ) : (
        <>
        <div className="md:hidden" data-testid="orders-mobile-list">
          {filtered.map((o) => (
            <div key={o.id} className="m-card cursor-pointer" onClick={() => setViewOrder(o)} data-testid={`order-card-${o.order_number}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-heading font-bold">{o.order_number}</p>
                  {isTeam && <p className="text-sm text-muted-foreground truncate">{o.client_name}</p>}
                  {o.patient_name && <p className="text-xs text-muted-foreground truncate">PTN: {o.patient_name}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold">{money(o.total)}</p>
                  <p className="text-xs text-muted-foreground">{o.items?.length || 0} {t("common.items").toLowerCase()} · {fmtDate(o.created_at)}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                <StatusBadge status={o.status} /><StatusBadge status={o.procurement_status} /><StatusBadge status={o.shipping_status} />
                {o.preferred_carrier === "brazil_courier" && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold">Brazil</span>}
              </div>
              {!isTeam && o.status === "pending" && (
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="rounded-sm h-9 flex-1" onClick={() => navigate(`/orders/${o.id}/edit`)}><PencilSimple size={14} className="mr-1" /> {t("orders.edit")}</Button>
                  <ConfirmDelete testid={`client-delete-order-m-${o.order_number}`} title={`Delete order ${o.order_number}?`} description="This permanently removes your pending order. This cannot be undone." onConfirm={() => removeClientOrder(o.id, o.order_number)} />
                </div>
              )}
              {!isTeam && o.status !== "pending" && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" className="rounded-sm h-9 w-full" onClick={() => navigate(`/orders/new?reorder=${o.id}`)} data-testid={`reorder-m-${o.order_number}`}><ArrowCounterClockwise size={14} className="mr-1" /> Reorder</Button>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className="py-10 text-center text-muted-foreground">No orders match "{search}".</p>}
        </div>
        <div className="space-y-0 hidden md:block">
          <div ref={topRef} onScroll={syncTop} className="overflow-x-auto border border-b-0 border-border rounded-t-md bg-card" data-testid="orders-top-scroll"><div style={{ width: scrollW, height: 1 }} /></div>
          <div ref={bodyRef} onScroll={syncBody} className="border border-border rounded-b-md overflow-x-auto bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                <th className="px-4 py-3 font-semibold">{t("common.order")}</th>
                {isTeam && <th className="px-4 py-3 font-semibold">Client</th>}
                {isTeam && <th className="px-4 py-3 font-semibold lap-hide">Ship Method</th>}
                <th className="px-4 py-3 font-semibold">{t("common.items")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.total")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.status")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.procurement")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.shipping")}</th>
                {isTeam && <th className="px-4 py-3 font-semibold">PO</th>}
                {isTeam && <th className="px-4 py-3 font-semibold">Invoice</th>}
                <th className="px-4 py-3 font-semibold">{t("common.placed")}</th>
                {isTeam && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
                {!isTeam && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr
                  key={o.id}
                  onClick={() => setViewOrder(o)}
                  className={`cursor-pointer hover:bg-primary/5 transition-colors ${i % 2 ? "bg-muted/30" : ""}`}
                  data-testid={`order-row-${o.order_number}`}
                >
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{o.order_number}</span>
                      {o.preferred_carrier === "brazil_courier" && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`badge-brazil-${o.order_number}`}>Brazil</span>}
                      {o.handling_type === "cool_box" && <span className="rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`badge-coolbox-${o.order_number}`}>Cool box</span>}
                      {o.consolidated_invoice_number && <Link to={`/consolidated-invoices/${o.consolidated_invoice_id}`} onClick={(e) => e.stopPropagation()} className="rounded-full bg-[#F5C64D]/20 text-[#7A5A00] border border-[#F5C64D] px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold hover:bg-[#F5C64D]/40" title="In consolidated invoice" data-testid={`badge-consolidated-${o.order_number}`}>{o.consolidated_invoice_number}</Link>}
                    </div>
                  </td>
                  {isTeam && <td className="px-4 py-3">{o.client_name}</td>}
                  {isTeam && <td className="px-4 py-3 lap-hide" data-testid={`order-ship-method-${o.order_number}`}>{o.preferred_carrier ? <CarrierBadge carrier={o.preferred_carrier} /> : <span className="text-muted-foreground">—</span>}</td>}
                  <td className="px-4 py-3 text-muted-foreground">{o.items?.length || 0}</td>
                  <td className="px-4 py-3">{money(o.total)}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3"><StatusBadge status={o.procurement_status} /></td>
                  <td className="px-4 py-3"><StatusBadge status={o.shipping_status} /></td>
                  {isTeam && (
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()} data-testid={`order-po-${o.order_number}`}>
                      {posFor(o.id).length ? posFor(o.id).map((p) => (
                        <Link key={p.id} to={`/purchase-orders/${p.id}`} className="block text-xs text-primary hover:underline" title={`${p.supplier_name || ""} · ${p.status}`}>{p.po_number}</Link>
                      )) : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  {isTeam && (
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()} data-testid={`order-invoice-${o.order_number}`}>
                      {invoicesFor(o.id).length ? invoicesFor(o.id).map((inv) => (
                        <Link key={inv.id} to={`/invoices/${inv.id}`} className="block text-xs text-primary hover:underline" title={inv.status}>{inv.invoice_number}</Link>
                      )) : <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(o.created_at)}</td>
                  {!isTeam && (
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {o.status === "pending" ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => navigate(`/orders/${o.id}/edit`)} data-testid={`edit-order-${o.order_number}`}><PencilSimple size={14} className="mr-1" /> {t("orders.edit")}</Button>
                          <ConfirmDelete
                            testid={`client-delete-order-${o.order_number}`}
                            title={`Delete order ${o.order_number}?`}
                            description="This permanently removes your pending order. This cannot be undone."
                            onConfirm={() => removeClientOrder(o.id, o.order_number)}
                          />
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => navigate(`/orders/new?reorder=${o.id}`)} data-testid={`reorder-${o.order_number}`}><ArrowCounterClockwise size={14} className="mr-1" /> Reorder</Button>
                      )}
                    </td>
                  )}
                  {isTeam && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <ConfirmDelete
                        testid={`delete-order-${o.order_number}`}
                        title={`Delete order ${o.order_number}?`}
                        description="This permanently removes the order along with its shipments and invoices. This cannot be undone."
                        onConfirm={() => removeOrder(o.id, o.order_number)}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 text-muted-foreground"><CaretRight size={16} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isTeam ? 13 : 9} className="px-4 py-10 text-center text-muted-foreground" data-testid="orders-no-results">No orders match "{search}".</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
        </>
      )}

      <Dialog open={!!viewOrder} onOpenChange={(o) => !o && setViewOrder(null)}>
        <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="order-view-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-3 flex-wrap">Order {viewOrder?.order_number}{viewOrder && <StatusBadge status={viewOrder.status} />}</DialogTitle></DialogHeader>
          {viewOrder && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isTeam && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Client</p><p className="font-medium mt-0.5">{viewOrder.client_name}</p></div>}
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Placed</p><p className="font-medium mt-0.5">{fmtDate(viewOrder.created_at)}</p></div>
                <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Ship method</p><p className="font-medium mt-0.5">{viewOrder.preferred_carrier ? <CarrierBadge carrier={viewOrder.preferred_carrier} /> : "—"}</p></div>
                {viewOrder.patient_name && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">PTN</p><p className="font-medium mt-0.5">{viewOrder.patient_name}</p></div>}
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={viewOrder.status} /><StatusBadge status={viewOrder.procurement_status} /><StatusBadge status={viewOrder.shipping_status} />
              </div>
              <div className="border border-border rounded-md overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground"><div className="col-span-7">Product</div><div className="col-span-2 text-right">Qty</div><div className="col-span-3 text-right">Unit</div></div>
                {(viewOrder.items || []).map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-border" data-testid={`order-view-item-${idx}`}><div className="col-span-7 font-medium">{it.name}{it.sku ? <span className="text-muted-foreground text-xs"> · {it.sku}</span> : null}</div><div className="col-span-2 text-right">{it.quantity}</div><div className="col-span-3 text-right">{money(it.unit_price)}</div></div>
                ))}
              </div>
              <div className="flex justify-end"><div className="w-56 flex justify-between border-t border-border pt-2 font-heading font-extrabold text-lg"><span>Total</span><span data-testid="order-view-total">{money(viewOrder.total)}</span></div></div>
              {isTeam && (
                <div className="grid grid-cols-2 gap-4 border-t border-border pt-3">
                  <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Purchase orders</p>{posFor(viewOrder.id).length ? posFor(viewOrder.id).map((p) => <Link key={p.id} to={`/purchase-orders/${p.id}`} className="block text-primary hover:underline text-sm">{p.po_number}</Link>) : <span className="text-muted-foreground">—</span>}</div>
                  <div><p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Invoices</p>{invoicesFor(viewOrder.id).length ? invoicesFor(viewOrder.id).map((inv) => <Link key={inv.id} to={`/invoices/${inv.id}`} className="block text-primary hover:underline text-sm">{inv.invoice_number}</Link>) : <span className="text-muted-foreground">—</span>}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setViewOrder(null)} data-testid="order-view-close">Close</Button>
            <Button className="rounded-sm" onClick={() => viewOrder && navigate(`/orders/${viewOrder.id}`)} data-testid="order-view-open-full"><Eye size={16} className="mr-2" /> Open full details</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
